import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// 导入路由配置
import authRoutes from './auth'
import platformRoutes from './platform'
import merchantRoutes from './merchant'
import profileRoutes from './profile'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/platform/merchants',
  },
  {
    path: '/dashboard',
    redirect: '/platform/merchants',
  },
  // 认证路由
  ...authRoutes,
  // 平台管理路由
  ...platformRoutes,
  // 组织管理路由
  ...merchantRoutes,
  // 个人中心
  ...profileRoutes,
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/common/NotFound.vue'),
    meta: { layout: 'blank' },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// 路由守卫
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()
  const resolveHomePath = () => {
    if (authStore.user?.userType === 1) {
      return '/platform/merchants'
    }
    if (authStore.user?.userType === 4) {
      return '/merchant/members'
    }
    return '/merchant/rules/points'
  }
  
  const requestedRootPaths = ['/', '/dashboard']
  if (requestedRootPaths.includes(to.path)) {
    const target = resolveHomePath()
    if (to.path !== target) {
      next(target)
      return
    }
  }
  
  if (authStore.user?.userType !== 1 && to.path.startsWith('/platform')) {
    const target = resolveHomePath()
    if (to.path !== target) {
      next(target)
      return
    }
  }
  
  // 检查路由是否需要认证
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }
  
  // 检查权限
  const requiredPermissions = to.meta.requiredPermissions as string[]
  if (requiredPermissions && requiredPermissions.length > 0) {
    if (!authStore.hasPermissions(requiredPermissions)) {
      next('/unauthorized')
      return
    }
  }

  const requiredModules = to.meta.requiredModules as string[] | undefined
  if (requiredModules && requiredModules.length > 0) {
    const enabledModules = authStore.user?.enabledModuleIds || []
    const missingModule = requiredModules.some((code) => !enabledModules.includes(code))
    if (missingModule) {
      next('/unauthorized')
      return
    }
  }
  
  // 如果已登录且访问登录页，跳转到首页
  if (to.name === 'Login' && authStore.isAuthenticated) {
    next(resolveHomePath())
    return
  }
  
  next()
})

// 全局后置守卫
router.afterEach((to) => {
  // 设置页面标题
  const title = to.meta.title as string
  if (title) {
    document.title = `${title} - 会员管理系统`
  } else {
    document.title = '会员管理系统'
  }
})

export default router
